/**
 * Job Card Component
 * Requirements: 6.1, 6.2, 6.3, 6.4, 7.1-7.8, 10.1
 */
import { useState, useEffect, useRef, memo } from 'react';
import { api } from '../services/api';
import type { UserJob, JobPhase, SubcomponentType, GenerationState, GenerationType } from '../types';
import { VALID_PHASES, SUBCOMPONENTS, GENERATION_TYPE_RESTRICTIONS } from '../types';
import { PHASE_COLORS, SUBCOMPONENT_LABELS, GENERATION_TYPE_LABELS } from '../constants';
import { ContextMenu, MenuItem } from './ContextMenu';
import {
  MapPin,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Loader2,
  Zap,
  SkipForward,
  Trash2,
  Pencil,
  Download,
  FileText,
  Eye,
  Brain,
  Settings,
  XCircle,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';

interface JobCardProps {
  job: UserJob;
  /** WARNING: onRefresh() causes full page reload (sets loading=true, unmounts component tree).
   *  Only use for destructive operations (delete, or as a fallback on error). For mutations
   *  (generate, type toggle, phase change), use onJobUpdate / onOptimisticPhaseChange. */
  onRefresh: () => void;
  onEditSubcomponent: (jobid: string, component: SubcomponentType) => void;
  onEditJob?: (jobid: string) => void;
  onGenerateResume?: (jobid: string) => void;
  onJobUpdate?: (updatedJob: UserJob) => void;
  /** Optimistic phase change — updates both the card's jobphase and the
   *  Dashboard-level phaseCounts in one shot, with no API round-trip. Same
   *  handler kanban drag-and-drop uses. Caller should call onRefresh only if
   *  the API call subsequently fails. */
  onOptimisticPhaseChange?: (jobid: string, newPhase: JobPhase) => void;
  selectedResume?: string | null;
}


// All allowed generation types (fallback if no restriction defined)
const ALL_GENERATION_TYPES: GenerationType[] = ['generate', 'embedding', 'manual', 'omit'];

const JobCardComponent = ({ job, onRefresh, onEditSubcomponent, onEditJob, onGenerateResume, onJobUpdate, onOptimisticPhaseChange, selectedResume }: JobCardProps) => {
  const [showMoveToMenu, setShowMoveToMenu] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingComponents, setGeneratingComponents] = useState<Set<SubcomponentType>>(new Set());
  const [generationStartTimes, setGenerationStartTimes] = useState<Map<SubcomponentType, number>>(new Map());
  // Track whether user has explicitly toggled the accordion, and persist the value
  const userToggledAccordion = useRef(false);
  const accordionValueRef = useRef(false);
  const [resumeSectionsExpanded, setResumeSectionsExpanded] = useState(accordionValueRef.current);

  // Context menu state for resume sections
  const [sectionContextMenu, setSectionContextMenu] = useState<{
    position: { x: number; y: number };
    component: SubcomponentType;
  } | null>(null);

  // Context menu state for file rows
  const [fileContextMenu, setFileContextMenu] = useState<{
    position: { x: number; y: number };
    fileType: 'resume' | 'cover';
  } | null>(null);

  const handleResumeSectionsToggle = () => {
    userToggledAccordion.current = true;
    setResumeSectionsExpanded(prev => {
      const next = !prev;
      accordionValueRef.current = next;
      return next;
    });
  };

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track retry counts per component to cap auto-retries
  const retryCountsRef = useRef<Map<SubcomponentType, number>>(new Map());

  // Show a toast for a given duration
  function showToast(message: string, durationMs = 5000) {
    setToastMessage(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), durationMs);
  }

  // Auto-retry for components in 'retry' state (resume still indexing)
  useEffect(() => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 3000;
    const SUBCOMPONENTS_LIST: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter', 'elevatorpitch'];
    const timers: ReturnType<typeof setTimeout>[] = [];

    SUBCOMPONENTS_LIST.forEach(comp => {
      const state = job[`state${comp}` as keyof UserJob] as GenerationState;
      if (state === 'retry') {
        const currentRetries = retryCountsRef.current.get(comp) || 0;
        if (currentRetries < MAX_RETRIES) {
          const msg = (job as any)[`msg${comp}`] as string | undefined;
          showToast(msg || 'Your resume is still being indexed. Retrying...', 4000);
          retryCountsRef.current.set(comp, currentRetries + 1);
          const timer = setTimeout(() => {
            handleGenerateSingle(comp);
          }, RETRY_DELAY_MS);
          timers.push(timer);
        } else {
          showToast('Resume indexing is taking longer than expected. Please try again in a moment.', 8000);
        }
      } else if (state === 'complete' || state === 'error') {
        // Reset retry count once resolved
        retryCountsRef.current.delete(comp);
      }
      // Show error messages from backend
      if (state === 'error') {
        const msg = (job as any)[`msg${comp}`] as string | undefined;
        if (msg) showToast(msg, 8000);
      }
    });

    return () => timers.forEach(t => clearTimeout(t));
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local timeout: clear spinning icons after 5 minutes
  useEffect(() => {
    const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

    const checkTimeouts = () => {
      const now = Date.now();
      const timedOutComponents: SubcomponentType[] = [];

      generationStartTimes.forEach((startTime, component) => {
        if ((now - startTime) > TIMEOUT_MS) {
          console.warn(`Generation timeout for ${job.jobid} ${component} - clearing spinner after 5 minutes`);
          timedOutComponents.push(component);
        }
      });

      if (timedOutComponents.length > 0) {
        // Remove timed-out components from both sets
        setGeneratingComponents(prev => {
          const next = new Set(prev);
          timedOutComponents.forEach(comp => next.delete(comp));
          return next;
        });
        setGenerationStartTimes(prev => {
          const next = new Map(prev);
          timedOutComponents.forEach(comp => next.delete(comp));
          return next;
        });
      }
    };

    // Check every 10 seconds
    const interval = setInterval(checkTimeouts, 10000);

    return () => clearInterval(interval);
  }, [generationStartTimes, job.jobid]);

  const [deleting, setDeleting] = useState(false);

  async function handleDeleteJob() {
    if (!confirm('Permanently delete this job? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.deleteJob(job.jobid);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete job:', error);
      alert('Failed to delete job. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  async function handlePhaseChange(phase: JobPhase) {
    // Optimistic UI: update immediately so the card visibly moves without
    // waiting for the API round-trip. Fall back to a full refresh only if
    // the write fails, so state snaps back to reality.
    setShowMoveToMenu(false);
    if (onOptimisticPhaseChange) {
      onOptimisticPhaseChange(job.jobid, phase);
    }
    try {
      await api.updateJobPhase(job.jobid, phase);
    } catch (error) {
      console.error('Failed to update phase:', error);
      alert(`Failed to move job to ${phase}. Please try again.`);
      onRefresh();
    }
  }

  async function handleGenerateAll() {
    // Guard: need a resume to generate
    const resumeid = selectedResume || job.resumeid;
    if (!resumeid) {
      alert('Please select a resume before generating. Use the resume dropdown in the header.');
      return;
    }

    setGenerating(true);
    // Only generate non-omit components that don't already have data
    // Exception: if job is in Queued phase, regenerate ALL sections (full regen)
    const isFullRegen = job.jobphase === 'Queued';
    const componentsToGenerate = SUBCOMPONENTS.filter(comp => {
      const type = job[`type${comp}` as keyof UserJob] as GenerationType;
      if (type === 'omit') return false;
      if (isFullRegen) return true; // Queued: regenerate everything
      // Skip sections that already have generated content
      const state = job[`state${comp}` as keyof UserJob] as GenerationState;
      if (state === 'complete' && hasData(comp)) return false;
      return true;
    });

    if (componentsToGenerate.length === 0) {
      // All sections already generated - trigger final files directly
      setGenerating(false);
      onGenerateResume?.(job.jobid);
      return;
    }

    setGeneratingComponents(new Set(componentsToGenerate));
    // Record start time for all components
    const now = Date.now();
    setGenerationStartTimes(new Map(componentsToGenerate.map(comp => [comp, now])));

    // Optimistic: move the card into "Generating" phase immediately and mark
    // each component as generating, so the UI moves the card without waiting
    // for the 10-20s API round-trip (ensure_job_tags can synchronously call
    // gen_tags Lambda for jobs missing tags). The server flips jobphase to
    // 'Generating' on the same fields, so this matches what's about to happen.
    // If the API call fails, onRefresh() below snaps state back to reality.
    const wasPhase = job.jobphase;
    if (job.jobphase !== 'Generating' && onOptimisticPhaseChange) {
      onOptimisticPhaseChange(job.jobid, 'Generating');
    }
    if (onJobUpdate) {
      const updates: Partial<UserJob> = { jobphase: 'Generating' };
      componentsToGenerate.forEach(comp => {
        (updates as any)[`state${comp}`] = 'generating';
      });
      onJobUpdate({ ...job, ...updates } as UserJob);
    }

    try {
      await api.generateAll(job.jobid, componentsToGenerate, resumeid);
      // Signal to parent that auto-final should start
      onGenerateResume?.(job.jobid);
      // Keep the optimistic UI state for a bit to ensure polling starts
      setTimeout(() => {
        setGenerating(false);
        setGeneratingComponents(new Set());
        setGenerationStartTimes(new Map());
      }, 1000);
    } catch (error) {
      console.error('Failed to generate all:', error);
      setGenerating(false);
      setGeneratingComponents(new Set());
      setGenerationStartTimes(new Map());
      // Roll back the optimistic phase move: snap back by refetching from API.
      if (wasPhase !== 'Generating' && onOptimisticPhaseChange) {
        onOptimisticPhaseChange(job.jobid, wasPhase);
      }
      onRefresh();
    }
  }

  async function handleGenerateSingle(component: SubcomponentType) {
    const resumeid = selectedResume || job.resumeid;
    if (!resumeid) {
      alert('Please select a resume before generating. Use the resume dropdown in the header.');
      return;
    }

    setGeneratingComponents(prev => new Set(prev).add(component));
    setGenerationStartTimes(prev => new Map(prev).set(component, Date.now()));
    // Mark the corresponding file as stale (content changed, final file not yet regenerated)
    // Elevator pitch is a pseudo-section — not rendered in resume or coverletter
    if (component !== 'elevatorpitch') {
      const staleTarget = component === 'coverletter' ? 'cover' : 'resume';
      setStaleFiles(prev => new Set(prev).add(staleTarget));
    }
    try {
      await api.generateSingle(job.jobid, component, resumeid);
      // Optimistically mark component as generating so Dashboard polling kicks in
      if (onJobUpdate) {
        const stateKey = `state${component}` as keyof UserJob;
        onJobUpdate({ ...job, [stateKey]: 'generating' } as UserJob);
      }
      // Keep the optimistic UI state for a bit to ensure polling starts
      setTimeout(() => {
        setGeneratingComponents(prev => {
          const next = new Set(prev);
          next.delete(component);
          return next;
        });
        setGenerationStartTimes(prev => {
          const next = new Map(prev);
          next.delete(component);
          return next;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to generate:', error);
      setGeneratingComponents(prev => {
        const next = new Set(prev);
        next.delete(component);
        return next;
      });
      setGenerationStartTimes(prev => {
        const next = new Map(prev);
        next.delete(component);
        return next;
      });
    }
  }

  async function handleSetType(component: SubcomponentType, newType: GenerationType) {
    try {
      await api.toggleGenerationType(job.jobid, component, newType);
      // Optimistic update: update the job in-place instead of triggering a full refresh
      if (onJobUpdate) {
        const typeKey = `type${component}` as keyof UserJob;
        const updatedJob = { ...job, [typeKey]: newType };
        onJobUpdate(updatedJob as UserJob);
      } else {
        // Fallback to full refresh if no onJobUpdate callback provided
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to set type:', error);
    }
  }

  // Check if component has data
  function hasData(component: SubcomponentType): boolean {
    const data = job[`data${component}` as keyof UserJob] as string | undefined;
    return !!data && data.trim().length > 0;
  }

  // Check if a final file exists (extended for txt and template)
  function hasFinalFile(type: 'resume' | 'cover', format: 'html' | 'pdf' | 'txt' | 'template'): boolean {
    if (type === 'resume') {
      if (format === 'html') return !!job.s3locresumehtml;
      if (format === 'pdf') return !!job.s3locresumepdf;
      if (format === 'txt') return !!job.s3locresumetxt;
      if (format === 'template') return !!job.s3locresumetemplate;
    } else {
      if (format === 'html') return !!job.s3loccoverletterhtml;
      if (format === 'pdf') return !!job.s3loccoverletterpdf;
      if (format === 'txt') return !!job.s3loccoverlettertxt;
    }
    return false;
  }

  // Get final file URL using custom domain (*.skillsnap.me)
  function getFinalFileUrl(type: 'resume' | 'cover', format: 'html' | 'pdf' | 'txt' | 'template'): string | null {
    let s3loc: string | undefined;
    if (type === 'resume') {
      if (format === 'html') s3loc = job.s3locresumehtml;
      else if (format === 'pdf') s3loc = job.s3locresumepdf;
      else if (format === 'txt') s3loc = job.s3locresumetxt;
      else if (format === 'template') s3loc = job.s3locresumetemplate;
    } else {
      if (format === 'html') s3loc = job.s3loccoverletterhtml;
      else if (format === 'pdf') s3loc = job.s3loccoverletterpdf;
      else if (format === 'txt') s3loc = job.s3loccoverlettertxt;
    }
    if (!s3loc) return null;

    // Strip s3://skillsnap-public-resumes/ prefix to get path
    const path = s3loc.replace(/^s3:\/\/skillsnap-public-resumes\//, '');
    // Path format: {username}/{company}/{jobtitlesafe}/{filename}
    const parts = path.split('/');
    if (parts.length >= 4) {
      const username = parts[0];
      const company = parts[1];
      const jobtitlesafe = parts[2];
      // Resume.html links to root directory (serves index.html = Resume.html)
      if (type === 'resume' && format === 'html') {
        return `https://${username}.skillsnap.me/${company}/${jobtitlesafe}`;
      }
      const filename = parts.slice(3).join('/');
      return `https://${username}.skillsnap.me/${company}/${jobtitlesafe}/${filename}`;
    }
    // Fallback: use cleaned path with CloudFront
    return `https://d2qz6zc5zvuvjc.cloudfront.net/${path}`;
  }

  // Check if any resume file exists
  function hasAnyResumeFile(): boolean {
    return hasFinalFile('resume', 'html') || hasFinalFile('resume', 'pdf') ||
      hasFinalFile('resume', 'txt') || hasFinalFile('resume', 'template');
  }

  // Check if any cover letter file exists
  function hasAnyCoverFile(): boolean {
    return hasFinalFile('cover', 'html') || hasFinalFile('cover', 'pdf') ||
      hasFinalFile('cover', 'txt');
  }

  // Build context menu items for a resume section row
  function buildSectionMenuItems(component: SubcomponentType): MenuItem[] {
    const currentType = (job[`type${component}` as keyof UserJob] as GenerationType) || 'embedding';
    const restrictions = GENERATION_TYPE_RESTRICTIONS[component];
    const allowedTypes: GenerationType[] = restrictions || ALL_GENERATION_TYPES;

    const items: MenuItem[] = [
      {
        label: 'View / Edit Content',
        icon: <Eye className="w-4 h-4" />,
        onClick: () => onEditSubcomponent(job.jobid, component),
      },
      {
        label: 'Regenerate Content',
        icon: <Zap className="w-4 h-4" />,
        onClick: () => handleGenerateSingle(component),
      },
      { divider: true },
      {
        label: 'Set Generation',
        submenu: allowedTypes.map(genType => ({
          label: GENERATION_TYPE_LABELS[genType],
          active: currentType === genType,
          onClick: () => handleSetType(component, genType),
        })),
      },
    ];

    return items;
  }

  // State for tracking file regeneration — tracks each type independently
  const [regeneratingFiles, setRegeneratingFiles] = useState<Set<string>>(new Set());
  // Track when sections have been regenerated but final files haven't been re-rendered yet
  const [staleFiles, setStaleFiles] = useState<Set<string>>(new Set());

  // Feedback state (initialized from job data)
  const [feedbackResume, setFeedbackResume] = useState<'U' | 'D' | undefined>(job.feedback_resume);
  const [feedbackCover, setFeedbackCover] = useState<'U' | 'D' | undefined>(job.feedback_coverletter);

  // Keep local feedback state in sync if job data changes from polling
  useEffect(() => {
    setFeedbackResume(job.feedback_resume);
  }, [job.feedback_resume]);
  useEffect(() => {
    setFeedbackCover(job.feedback_coverletter);
  }, [job.feedback_coverletter]);

  // Error banner state for file regeneration failures
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleRegenerateFiles(fileType: 'resume' | 'cover') {
    setRegeneratingFiles(prev => new Set(prev).add(fileType));
    setFileError(null);
    // Clear stale indicator — user is regenerating the final file
    setStaleFiles(prev => { const next = new Set(prev); next.delete(fileType); return next; });
    try {
      await api.generateFinalFiles(job.jobid, fileType);
      // Polling will pick up updated files — no full page refresh needed
    } catch (error: any) {
      console.error('Failed to regenerate files:', error);
      const detail = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unknown error';
      setFileError(`Regeneration failed: ${detail}`);
    } finally {
      // Keep spinner briefly so polling can start
      setTimeout(() => {
        setRegeneratingFiles(prev => {
          const next = new Set(prev);
          next.delete(fileType);
          return next;
        });
      }, 2000);
    }
  }

  // Download a file via fetch + blob to trigger a real browser download
  async function downloadFile(url: string, filename: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // Submit feedback for a file type
  async function handleFeedback(fileType: 'resume' | 'cover', result: 'U' | 'D') {
    const topic = fileType === 'resume' ? 'resume' : 'coverletter';
    const currentFeedback = fileType === 'resume' ? feedbackResume : feedbackCover;

    // Toggle off if same button clicked again
    const newResult = currentFeedback === result ? undefined : result;

    // Optimistic update
    if (fileType === 'resume') {
      setFeedbackResume(newResult);
    } else {
      setFeedbackCover(newResult);
    }

    // Also update the job object optimistically so polling doesn't revert it
    if (onJobUpdate) {
      const feedbackField = fileType === 'resume' ? 'feedback_resume' : 'feedback_coverletter';
      onJobUpdate({ ...job, [feedbackField]: newResult } as any);
    }

    // If toggling off, we still submit the last clicked value (server will store it)
    // For a true "remove", we would need a separate endpoint, but for MVP we just toggle
    if (newResult) {
      try {
        await api.submitFeedback(job.jobid, topic, newResult);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        // Revert on error
        if (fileType === 'resume') {
          setFeedbackResume(currentFeedback);
        } else {
          setFeedbackCover(currentFeedback);
        }
      }
    }
  }

  // Build context menu items for a file row
  function buildFileMenuItems(fileType: 'resume' | 'cover'): MenuItem[] {
    const items: MenuItem[] = [];

    // Regenerate Files option at the top — only regenerates this file type
    const isRegenerating = regeneratingFiles.has(fileType);
    items.push({
      label: isRegenerating ? 'Regenerating...' : `Regenerate ${fileType === 'resume' ? 'Resume' : 'Cover Letter'}`,
      icon: <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />,
      disabled: isRegenerating,
      onClick: !isRegenerating ? () => handleRegenerateFiles(fileType) : undefined,
    });
    items.push({ divider: true });

    const hasHtml = hasFinalFile(fileType, 'html');
    const hasPdf = hasFinalFile(fileType, 'pdf');
    const hasTxt = hasFinalFile(fileType, 'txt');
    const hasTemplate = fileType === 'resume' && hasFinalFile('resume', 'template');

    // View HTML
    items.push({
      label: 'View HTML',
      icon: <ExternalLink className="w-4 h-4" />,
      disabled: !hasHtml,
      onClick: hasHtml ? () => {
        const url = getFinalFileUrl(fileType, 'html');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // View PDF
    items.push({
      label: 'View PDF',
      icon: <ExternalLink className="w-4 h-4" />,
      disabled: !hasPdf,
      onClick: hasPdf ? () => {
        const url = getFinalFileUrl(fileType, 'pdf');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // View Text
    items.push({
      label: 'View Text',
      icon: <FileText className="w-4 h-4" />,
      disabled: !hasTxt,
      onClick: hasTxt ? () => {
        const url = getFinalFileUrl(fileType, 'txt');
        if (url) window.open(url, '_blank');
      } : undefined,
    });

    // Download Template (resume only)
    if (fileType === 'resume') {
      items.push({
        label: 'Download Template',
        icon: <Download className="w-4 h-4" />,
        disabled: !hasTemplate,
        onClick: hasTemplate ? () => {
          const url = getFinalFileUrl('resume', 'template');
          if (url) {
            const filename = url.split('/').pop() || 'resume-template.yaml';
            downloadFile(url, filename);
          }
        } : undefined,
      });
    }

    // Feedback row
    items.push({ divider: true });

    const hasAnyFile = fileType === 'resume' ? hasAnyResumeFile() : hasAnyCoverFile();
    const currentFeedback = fileType === 'resume' ? feedbackResume : feedbackCover;

    items.push({
      customRender: (
        <div className={`flex items-center gap-2 ${hasAnyFile ? '' : 'opacity-40 pointer-events-none'}`}>
          <span className="text-xs text-gray-500 flex-1">Happy with this?</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasAnyFile) handleFeedback(fileType, 'U');
            }}
            className={`p-1 rounded transition-colors ${
              currentFeedback === 'U'
                ? 'text-green-600 bg-green-50'
                : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
            }`}
            title="Thumbs up"
            aria-label="Thumbs up"
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasAnyFile) handleFeedback(fileType, 'D');
            }}
            className={`p-1 rounded transition-colors ${
              currentFeedback === 'D'
                ? 'text-red-600 bg-red-50'
                : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
            }`}
            title="Thumbs down"
            aria-label="Thumbs down"
          >
            <ThumbsDown className="w-4 h-4" />
          </button>
        </div>
      ),
    });

    return items;
  }

  // Filter out current phase and Trash for move-to options (Trash shown separately)
  const moveToPhases = VALID_PHASES.filter(p => p !== job.jobphase && p !== 'Trash');

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible" data-tutorial="job-card">
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        {/* Company Name (left) and Phase/Move To (right) */}
        <div className="flex items-start justify-between mb-2">
          {/* Company Name - Large */}
          <p className="text-lg font-bold text-gray-800 truncate flex-1 mr-3">{job.jobcompany}</p>

          {/* Phase and Move To - Right justified */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${PHASE_COLORS[job.jobphase]}`}>
              {job.jobphase}
            </span>
            <div className="relative">
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-500">Move To</span>
                <button
                  onClick={() => setShowMoveToMenu(!showMoveToMenu)}
                  onBlur={() => setTimeout(() => setShowMoveToMenu(false), 150)}
                  aria-haspopup="true"
                  aria-expanded={showMoveToMenu}
                  className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center"
                >
                  Select
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </div>
              {showMoveToMenu && (
                <div role="menu" className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {moveToPhases.map((phase) => (
                    <button
                      key={phase}
                      role="menuitem"
                      onMouseDown={(e) => { e.preventDefault(); handlePhaseChange(phase); }}
                      className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100"
                    >
                      {phase}
                    </button>
                  ))}
                  <div className="border-t border-gray-200">
                    <button
                      role="menuitem"
                      onMouseDown={(e) => { e.preventDefault(); handlePhaseChange('Trash'); }}
                      className="w-full px-3 py-1.5 text-left text-xs hover:bg-red-50 text-red-600 flex items-center"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Trash
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Job Title */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {job.joburl ? (
                <a
                  href={job.joburl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary-600 flex items-center"
                >
                  <span className="truncate">{job.jobtitle}</span>
                  <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                </a>
              ) : (
                job.jobtitle
              )}
            </h3>
          </div>
          {onEditJob && (
            <button
              onClick={() => onEditJob(job.jobid)}
              className="flex items-center text-gray-500 hover:text-primary-600 ml-2"
              title="Edit Job"
              aria-label="Edit Job"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Meta info */}
        <div className="flex flex-col gap-1 mt-2 text-xs text-gray-500">
          {/* Row 1: Location, Salary */}
          {(job.joblocation || job.jobsalary) && (
            <div className="flex items-center gap-4">
              {job.joblocation && (
                <span className="flex items-center">
                  <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span>{job.joblocation}</span>
                </span>
              )}
              {job.jobsalary && (
                <span className="flex items-center">
                  <span>{job.jobsalary}</span>
                </span>
              )}
            </div>
          )}
          {/* Row 2: Age, Source */}
          <div className="flex items-center gap-4">
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
              {job.postingAge !== undefined ? `${job.postingAge}d ago` : job.jobposteddate}
            </span>
            <span className="text-gray-500">
              {job.joblistid ? 'LinkedIn Job Alert' : job.joburl ? 'URL Added' : 'Manually Added'}
            </span>
          </div>
        </div>

        {/* Tags - Row 3 */}
        {job.jobtags && job.jobtags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {job.jobtags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded"
              >
                {tag}
              </span>
            ))}
            {job.jobtags.length > 3 && (
              <span className="text-[10px] text-gray-500">+{job.jobtags.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Generate Resume Button and Resume Sections Accordion */}
      <div className="p-3 border-b border-gray-100">
        {/* Generate Resume + Skip Buttons */}
        <div className="flex gap-1 mb-2">
          <button
            data-tutorial="generate-resume"
            onClick={handleGenerateAll}
            disabled={generating}
            className="flex-[3] flex items-center justify-center p-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {generating ? (
              <span role="status" aria-label="Generating">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              </span>
            ) : (
              <Zap className="w-4 h-4 mr-1" />
            )}
            Generate Resume
          </button>
          <button
            onClick={() => handlePhaseChange('Skipped')}
            className="flex-1 flex items-center justify-center p-1.5 bg-gray-200 text-gray-600 text-xs rounded hover:bg-gray-300"
            title="Skip this job"
          >
            <SkipForward className="w-3 h-3 mr-1" />
            Skip
          </button>
          {job.jobphase === 'Trash' && (
            <button
              onClick={handleDeleteJob}
              disabled={deleting}
              className="flex-1 flex items-center justify-center p-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
              title="Permanently delete this job"
            >
              {deleting ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3 mr-1" />
              )}
              Delete
            </button>
          )}
        </div>

        {/* Resume Sections Accordion */}
        <div className="border border-gray-200 rounded-lg" data-tutorial="resume-sections">
          <button
            type="button"
            onClick={handleResumeSectionsToggle}
            aria-expanded={resumeSectionsExpanded}
            className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
          >
            <span className="text-xs font-medium text-gray-700">Resume Sections</span>
            {resumeSectionsExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>

          {resumeSectionsExpanded && (
            <div className="p-2">
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-1" role="status" aria-live="polite">
                {SUBCOMPONENTS.map((component) => {
                  const state = job[`state${component}` as keyof UserJob] as GenerationState;
                  const type = (job[`type${component}` as keyof UserJob] as GenerationType) || 'embedding';
                  const isGenerating = generatingComponents.has(component) || state === 'generating';
                  const isRetry = state === 'retry';
                  const isComplete = hasData(component) || state === 'complete';
                  const isOmit = type === 'omit';

                  // Determine background color
                  const isHighlighted = sectionContextMenu?.component === component;
                  let bgClass = 'bg-gray-100'; // not generated
                  if (isOmit) {
                    bgClass = 'bg-gray-100';
                  } else if (isGenerating || isRetry) {
                    bgClass = isRetry ? 'bg-amber-50 animate-pulse' : 'bg-purple-50 animate-pulse';
                  } else if (isComplete) {
                    bgClass = 'bg-green-50';
                  }

                  return (
                    <div
                      key={component}
                      className={`flex items-center p-1.5 rounded cursor-pointer select-none ${bgClass} ${isHighlighted ? 'ring-2 ring-primary-500 ring-inset' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setSectionContextMenu({
                          position: { x: e.clientX, y: e.clientY },
                          component,
                        });
                      }}
                    >
                      {/* Generation type indicator icon */}
                      {(type === 'embedding' || type === 'generate') && (
                        <Brain className="w-3 h-3 text-purple-500 mr-1 flex-shrink-0" />
                      )}
                      {type === 'manual' && (
                        <Settings className="w-3 h-3 text-gray-500 mr-1 flex-shrink-0" />
                      )}
                      {type === 'omit' && (
                        <XCircle className="w-3 h-3 text-red-400 mr-1 flex-shrink-0" />
                      )}
                      <span
                        className={`text-xs truncate ${isOmit ? 'text-gray-500 line-through' : 'text-gray-700'}`}
                      >
                        {SUBCOMPONENT_LABELS[component]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Files List - flat list below sections */}
      <div className="p-3" data-tutorial="files-list">
        <span className="text-xs font-medium text-gray-700 mb-2 block">Files</span>
        <div className="grid grid-cols-2 gap-1" role="status" aria-live="polite">
          {(() => {
            // Split-button styling: main area = View PDF (most common), chevron = menu.
            // Renders the same shape regardless of state (regenerating / ready / empty)
            // so the "this is a button" affordance is consistent.
            const buttonClass = (state: 'ready' | 'regenerating' | 'empty', menuOpen: boolean) => {
              const base = 'flex items-stretch rounded-md select-none overflow-hidden border transition-shadow';
              const ring = menuOpen ? ' ring-2 ring-primary-500' : '';
              if (state === 'regenerating') return `${base} bg-purple-100 border-purple-300 animate-pulse${ring}`;
              if (state === 'ready') return `${base} bg-green-100 border-green-400 shadow-sm hover:shadow${ring}`;
              return `${base} bg-gray-100 border-gray-200${ring}`;
            };
            const mainClass = (state: 'ready' | 'regenerating' | 'empty') => {
              const base = 'flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-left transition-colors';
              if (state === 'regenerating') return `${base} text-purple-900 cursor-wait`;
              if (state === 'ready') return `${base} text-green-900 hover:bg-green-200 cursor-pointer`;
              return `${base} text-gray-500 cursor-default`;
            };
            const chevronClass = (state: 'ready' | 'regenerating' | 'empty') => {
              const base = 'px-1.5 flex items-center border-l transition-colors';
              if (state === 'regenerating') return `${base} border-purple-300 text-purple-700`;
              if (state === 'ready') return `${base} border-green-400 text-green-800 hover:bg-green-200 cursor-pointer`;
              return `${base} border-gray-200 text-gray-500 hover:bg-gray-200 cursor-pointer`;
            };

            const resumeState: 'ready' | 'regenerating' | 'empty' =
              regeneratingFiles.has('resume') ? 'regenerating'
              : hasAnyResumeFile() && !staleFiles.has('resume') ? 'ready'
              : 'empty';
            const coverState: 'ready' | 'regenerating' | 'empty' =
              regeneratingFiles.has('cover') ? 'regenerating'
              : hasAnyCoverFile() && !staleFiles.has('cover') ? 'ready'
              : 'empty';

            return (
              <>
                {/* Resume — main click = View PDF, chevron = options menu */}
                <div className={buttonClass(resumeState, fileContextMenu?.fileType === 'resume')}>
                  <button
                    type="button"
                    className={mainClass(resumeState)}
                    onClick={() => {
                      if (resumeState !== 'ready') return;
                      const url = getFinalFileUrl('resume', 'pdf');
                      if (url) window.open(url, '_blank');
                    }}
                    disabled={resumeState !== 'ready'}
                    title={resumeState === 'ready' ? 'View Resume PDF' : 'No resume generated yet'}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Resume</span>
                  </button>
                  <button
                    type="button"
                    className={chevronClass(resumeState)}
                    onClick={(e) => {
                      e.preventDefault();
                      setFileContextMenu({
                        position: { x: e.clientX, y: e.clientY },
                        fileType: 'resume',
                      });
                    }}
                    title="More resume options"
                    aria-label="More resume options"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Cover Letter — main click = View PDF, chevron = options menu */}
                <div className={buttonClass(coverState, fileContextMenu?.fileType === 'cover')}>
                  <button
                    type="button"
                    className={mainClass(coverState)}
                    onClick={() => {
                      if (coverState !== 'ready') return;
                      const url = getFinalFileUrl('cover', 'pdf');
                      if (url) window.open(url, '_blank');
                    }}
                    disabled={coverState !== 'ready'}
                    title={coverState === 'ready' ? 'View Cover Letter PDF' : 'No cover letter generated yet'}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Cover Letter</span>
                  </button>
                  <button
                    type="button"
                    className={chevronClass(coverState)}
                    onClick={(e) => {
                      e.preventDefault();
                      setFileContextMenu({
                        position: { x: e.clientX, y: e.clientY },
                        fileType: 'cover',
                      });
                    }}
                    title="More cover letter options"
                    aria-label="More cover letter options"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            );
          })()}
        </div>
        {fileError && (
          <div className="mt-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center justify-between">
            <span>{fileError}</span>
            <button onClick={() => setFileError(null)} className="ml-2 text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}
      </div>

      {/* Section Context Menu */}
      {sectionContextMenu && (
        <ContextMenu
          items={buildSectionMenuItems(sectionContextMenu.component)}
          position={sectionContextMenu.position}
          onClose={() => setSectionContextMenu(null)}
        />
      )}

      {/* File Context Menu */}
      {fileContextMenu && (
        <ContextMenu
          items={buildFileMenuItems(fileContextMenu.fileType)}
          position={fileContextMenu.position}
          onClose={() => setFileContextMenu(null)}
        />
      )}

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm px-4 py-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span>{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="ml-2 text-gray-400 hover:text-white flex-shrink-0"
          >&times;</button>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render if the job data or handlers have changed
export const JobCard = memo(JobCardComponent, (prevProps, nextProps) => {
  // Re-render if job data changed
  if (JSON.stringify(prevProps.job) !== JSON.stringify(nextProps.job)) {
    return false;
  }
  // Re-render if selectedResume changed (affects which resume is used for generation)
  if (prevProps.selectedResume !== nextProps.selectedResume) {
    return false;
  }
  // Don't re-render if only the handler references changed (they're stable from parent)
  return true;
});
